"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Award, CircleCheck as CheckCircle2, Clock, FileCheck, PhoneCall, ShieldCheck, Star, Wrench } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const trustBadges = [{
  id: "badge-1",
  title: "Factory-Trained Technicians",
  description: "Certified expertise in Moen 1222, 1225, and 1200 Positemp cartridges."
}, {
  id: "badge-2",
  title: "20+ Years Experience",
  description: "Serving Jonesboro homes with precision Moen fixture diagnostics and repairs."
}, {
  id: "badge-3",
  title: "Genuine OEM Parts Stocked",
  description: "Always loaded on service trucks to ensure same-day emergency repairs."
}, {
  id: "badge-4",
  title: "Flo by Moen Specialists",
  description: "Licensed installation and troubleshooting of smart water shutoff systems."
}];
const faqs = [{
  id: "faq-1",
  question: "Why is my Moen shower faucet dripping or leaking from the handle?",
  answer: "Most Moen single-handle shower leaks are caused by a worn-out internal cartridge (commonly the 1222 Positemp or 1225 cartridge). Hard water deposits and age degrade the rubber seals over time. We carry authentic Moen replacement cartridges on our service trucks and can resolve this in a single visit without wall damage."
}, {
  id: "faq-2",
  question: "Do you install and service Flo by Moen smart water shutoff valves?",
  answer: "Yes. Our master plumbers are certified in the sizing, pipe integration, and calibration of Flo by Moen smart leak detectors. We ensure proper electrical bonding, leak testing, and Wi-Fi pairing so your home stays guarded against catastrophic burst pipes 24/7."
}, {
  id: "faq-3",
  question: "Are Moen replacement parts covered under warranty?",
  answer: "Moen offers limited lifetime warranties on many original residential fixtures. When repairing or replacing components, we use genuine Moen OEM hardware to maintain your manufacturer warranty coverage, complemented by our own 100% satisfaction workmanship guarantee."
}, {
  id: "faq-4",
  question: "Can you fix low water pressure in a Moen kitchen pulldown faucet?",
  answer: "Low pressure in Moen kitchen faucets typically stems from clogged aerator screens, mineral buildup in the Reflex hose, or a malfunctioning check valve inside the spray head. We quickly isolate the blockage, clean or replace the aerator/hose assembly, and restore standard flow pressure."
}, {
  id: "faq-5",
  question: "How fast can an emergency plumber arrive in Jonesboro for a leaking valve?",
  answer: "Our technicians operate locally in Jonesboro, GA, and the surrounding Clayton County communities. For emergency shutoffs and severe fixture leaks, we dispatch immediately and typically arrive within 45 to 60 minutes."
}];
const testimonials = [{
  id: "test-1",
  name: "Marcus Holloway",
  location: "Jonesboro, GA",
  initials: "MH",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_marcus_holloway.webp",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_marcus_holloway.webp-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_marcus_holloway.webp-wvc-sizes",
  quote: "Our Moen shower valve refused to turn off late on a Sunday. The plumber was here in 35 minutes with the exact Positemp cartridge in his truck. Professional, fair, and fast."
}, {
  id: "test-2",
  name: "Elena Rostova",
  location: "Lake City / Jonesboro Area",
  initials: "ER",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_elena_rostova.webp",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_elena_rostova.webp-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/customer_elena_rostova.webp-wvc-sizes",
  quote: "They installed the Flo by Moen smart valve on our main supply line. Clean pipe work, clear instructions on the app, and zero mess left behind. Truly reliable plumbing pros."
}];
export default function MoenTrust() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="moenfaqandtrust" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-14 sm:mb-20">
          <Badge variant="outline" className="mb-4 gap-1.5 px-3 py-1 font-semibold text-primary border-primary/30">
            <ShieldCheck className="size-4 text-primary" />
            Moen Certified Fixture Specialists
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            Moen Plumbing Expertise & Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg">
            Certified installation, rapid cartridge replacement, and 24/7 leak repairs for Moen kitchen, bath, and smart water shutoff systems across Jonesboro.
          </p>
        </div>

        {/* Trust Badges Grid */}
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          {trustBadges.map((badge, i) => <motion.div key={badge.id} variants={itemVariants} data-index={i}>
              <Card className="h-full border border-border bg-card shadow-xs hover:shadow-md transition-shadow">
                <CardContent className="p-6 flex flex-col justify-between h-full">
                  <div className="space-y-3">
                    <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      {i === 0 && <Award className="size-6" />}
                      {i === 1 && <Clock className="size-6" />}
                      {i === 2 && <Wrench className="size-6" />}
                      {i === 3 && <FileCheck className="size-6" />}
                    </div>
                    <h3 className="text-lg font-semibold text-card-foreground">
                      {badge.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {badge.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Two Column Layout: FAQ & Trust Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* FAQ Accordion Column */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Common Moen Repair Questions
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Clear answers regarding cartridge codes, water pressure issues, warranties, and emergency callouts.
              </p>
            </div>

            <Accordion type="single" collapsible defaultValue="faq-1" className="w-full space-y-3">
              {faqs.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="border border-border bg-card rounded-lg px-5 py-1">
                  <AccordionTrigger className="text-left font-semibold text-foreground hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {/* Right Column: Testimonials & Fast Dispatch Box */}
          <div className="lg:col-span-5 space-y-6">
            {/* Experience Card */}
            <Card className="border border-border bg-secondary/50">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-accent text-accent-foreground">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-foreground">
                      Jonesboro Certified Moen Pros
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Over 1,200+ Moen valves and fixtures restored in Clayton County
                    </p>
                  </div>
                </div>
                <Separator className="my-2" />
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Exact OEM cartridge match guarantee</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>No-damage puller tools for frozen brass stems</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Upfront flat-rate pricing before work begins</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonials */}
            <div className="space-y-4">
              <h4 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Star className="size-4 fill-accent text-accent" />
                Recent Moen Customer Reviews
              </h4>

              {testimonials.map((t, i) => <Card key={t.id} data-index={i} className="border border-border bg-card shadow-xs">
                  <CardContent className="p-5 space-y-3">
                    <div className="flex items-center gap-1 text-accent">
                      <Star className="size-3.5 fill-current" />
                      <Star className="size-3.5 fill-current" />
                      <Star className="size-3.5 fill-current" />
                      <Star className="size-3.5 fill-current" />
                      <Star className="size-3.5 fill-current" />
                    </div>
                    <p className="text-sm italic text-card-foreground leading-relaxed">
                      "{t.quote}"
                    </p>
                    <div className="flex items-center gap-3 pt-2">
                      <SafeHydrate><Avatar className="size-9 border border-border">
                        <AvatarImage src={t.avatarSrc} data-wvc-srcset={t.avatarSrcset} data-wvc-sizes={t.avatarSizes} alt={t.name} />
                        <AvatarFallback className="bg-muted text-muted-foreground text-xs font-semibold">
                          {t.initials}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div>
                        <div className="text-sm font-semibold text-foreground leading-none">
                          {t.name}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {t.location}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>

            {/* Emergency CTA Banner */}
            <div className="rounded-xl bg-primary text-primary-foreground p-6 space-y-4 shadow-sm">
              <div className="flex items-center gap-2 text-accent font-semibold text-sm">
                <Clock className="size-4" />
                <span>24/7 Immediate Emergency Service</span>
              </div>
              <h4 className="text-xl font-bold leading-tight">
                Got an urgent Moen valve leak or shower emergency?
              </h4>
              <p className="text-primary-foreground/80 text-sm">
                Speak directly with an on-call licensed plumber in Jonesboro.
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <Button asChild variant="secondary" className="w-full sm:w-auto font-semibold gap-2 shadow-sm">
                  <a href="tel:4708479076">
                    <PhoneCall className="size-4" />
                    Call Now: (470) 847-9076
                  </a>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto border-primary-foreground/30 text-primary-foreground bg-primary/20 hover:bg-primary/40">
                  <Link to="/contact/">
                    Book Service Online
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}